<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type { CartDetailsItemResponse } from '@/types/carts-transactions-api';

import ContactFieldInput from '@/components/cart/v2/cart-item/ContactFieldInput.vue';
import MobileModal from '@/components/MobileModal.vue';
import { useContactFieldsForm } from '@/composables/carts/useContactFieldsForm';

const props = defineProps<{
  item: CartDetailsItemResponse;
  cartsTransactionsApi: CartsTransactionsApi;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  'update:completed': [value: boolean];
}>();

const { t } = useI18n();

const isOpen = ref(false);

const {
  isCompleted,
  isSaving,
  saveError,
  renderableGroups,
  summaryText,
  isFormValid,
  getFieldValue,
  setFieldValue,
  resetFormToSaved,
  saveForm,
} = useContactFieldsForm(
  () => props.item,
  () => props.cartsTransactionsApi,
  value => emit('update:completed', value),
);

function openModal() {
  if (props.disabled)
    return;
  resetFormToSaved();
  isOpen.value = true;
}

function closeModal() {
  resetFormToSaved();
  isOpen.value = false;
}

async function handleApply() {
  await saveForm(t('cart.contactFields.saveError'));
  if (isCompleted.value)
    closeModal();
}
</script>

<template>
  <div
    class="state-container"
    :class="{ completed: isCompleted, disabled }"
    role="button"
    :tabindex="disabled ? -1 : 0"
    :aria-label="isCompleted ? t('cart.contactFields.openFormCompleted') : t('cart.contactFields.openForm')"
    :aria-expanded="isOpen"
    :aria-disabled="disabled || undefined"
    @click="openModal"
    @keydown.enter="openModal"
    @keydown.space.prevent="openModal"
  >
    <div>
      <p class="title">
        {{ t('cart.contactFields.title') }}
      </p>
      <p v-if="summaryText" class="units">
        {{ summaryText }}
      </p>
      <p v-if="!isCompleted" class="warning">
        {{ t('cart.contactFields.complete') }}
      </p>
      <p v-else class="success">
        {{ t('cart.contactFields.completed') }}
      </p>
    </div>
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M11 20L17 14L11 8" stroke="#252525" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  </div>

  <MobileModal
    :is-open="isOpen"
    :title="t('cart.contactFields.title')"
    @close="closeModal"
  >
    <template #body>
      <div v-if="renderableGroups.length === 0" class="debug-empty">
        <p>{{ t('cart.contactFields.debugEmpty') }}</p>
        <p class="debug-info">
          {{ t('cart.contactFields.debugInfo') }}
        </p>
      </div>

      <div
        v-for="(group, groupIndex) in renderableGroups"
        :key="group.groupKey"
        class="participant-group"
      >
        <h4 v-if="group.title" class="participant-title">
          {{ group.title }}
        </h4>

        <div
          v-for="formField in group.formFields"
          :key="formField.formKey"
          class="form-field"
        >
          <label :for="`field-${groupIndex}-${formField.formKey}`" class="field-label">
            {{ formField.field.name }}
            <span v-if="formField.field.required" class="required-indicator">*</span>
          </label>

          <ContactFieldInput
            :field="formField.field"
            :input-id="`field-${groupIndex}-${formField.formKey}`"
            :model-value="getFieldValue(formField.formKey, formField.field)"
            @update:model-value="setFieldValue(formField.formKey, $event)"
          />
        </div>

        <hr v-if="groupIndex < renderableGroups.length - 1" class="group-separator" aria-hidden="true">
      </div>
    </template>
    <template #footer>
      <p v-if="saveError" class="save-error" role="alert" aria-live="assertive">
        {{ saveError }}
      </p>
      <button type="button" class="yb-apply-button apply-button-sm" :disabled="!isFormValid || isSaving" :aria-busy="isSaving" @click="handleApply">
        <span v-if="isSaving">{{ t('cart.contactFields.saving') }}</span>
        <span v-else>{{ t('cart.contactFields.apply') }}</span>
      </button>
    </template>
  </MobileModal>
</template>

<style scoped lang="css">
.state-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffdada;
  border: 1px solid #c10007;
  padding: var(--yb-spacing-sm);
  border-radius: var(--yb-radius-sm);
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  &.completed {
    background: #d4edda;
    border-color: #008236;
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-weight: var(--yb-text-bold);
  }
}

.title {
  font-weight: var(--yb-text-bold);
}

.units {
  font-size: var(--yb-text-sm);
}

.warning {
  font-size: var(--yb-text-sm);
  color: #ea3532;
}

.success {
  font-size: var(--yb-text-sm);
  color: #28a745;
}

.debug-empty {
  text-align: center;
  padding: var(--yb-spacing-base);
  color: var(--yb-color-darkgray);
}

.debug-info {
  font-size: var(--yb-text-xs);
  color: var(--yb-color-mediumgray);
  margin-top: var(--yb-spacing-xs);
}

.participant-group {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
}

.participant-title {
  font-weight: var(--yb-text-semi-bold);
  font-size: var(--yb-text-base);
  color: var(--yb-color-black);
  margin: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-black);
}

.required-indicator {
  color: #ea3532;
}

.save-error {
  font-size: var(--yb-text-sm);
  color: #ea3532;
  margin-bottom: var(--yb-spacing-xs);
  text-align: center;
}

.apply-button-sm {
  font-size: var(--yb-text-sm);
}
</style>
